import React, { useState, useEffect, useCallback } from 'react';
import masterService from '../../services/masterService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import ConfirmModal from './components/ConfirmModal';
import CommonPagination from '../../components/common/CommonPagination';
import { PAGINATION_CONFIG } from '../../config/paginationConfig';

const FORMAT_OPTIONS = [
  { value: 'Numeric', label: 'Numeric (Quantitative)' },
  { value: 'Text', label: 'Free Text / Narrative' },
  { value: 'Option', label: 'Fixed Options (Pick List)' },
  { value: 'Formula', label: 'Calculated / Formula' }
];

const INITIAL_OBSERVATION = {
  id: 0,
  labObservationId: '',
  name: '',
  readingFormat: 'Numeric',
  defaultValue: '',
  unit: '',
  roundUpto: 2,
  minCritical: '',
  maxCritical: '',
  formula: '',
  resultRequired: true,
  isComment: false,
  isActive: true,
  ranges: []
};

export const ObservationMasterPage = ({ showToast }) => {
  // View Mode: 'form' | 'list'
  const [viewMode, setViewMode] = useState('form');
  const [isEditMode, setIsEditMode] = useState(false);

  // Form State
  const [formData, setFormData] = useState(INITIAL_OBSERVATION);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // List State
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formatFilter, setFormatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINATION_CONFIG.defaultPageSize);

  // Reset to page 1 on filter/search change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, formatFilter, statusFilter]);

  // Confirmation Modal
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Load Observations for List
  const fetchObservations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await masterService.getObservations({
        page,
        pageSize,
        search: searchQuery.trim() || undefined,
        readingFormat: formatFilter || undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active'
      });
      if (res.success && res.data) {
        setItems(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
      }
    } catch (err) {
      showToast?.('Failed to load observation analytes.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, formatFilter, statusFilter, showToast]);

  useEffect(() => {
    if (viewMode === 'list') {
      fetchObservations();
    }
  }, [viewMode, fetchObservations]);

  // Handle Form Change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Switch to clean Form
  const handleAddNew = () => {
    const autoCode = `OBS_${Math.floor(1000 + Math.random() * 9000)}`;
    setFormData({
      ...INITIAL_OBSERVATION,
      labObservationId: autoCode
    });
    setErrors({});
    setIsEditMode(false);
    setViewMode('form');
  };

  // Switch to Edit Form
  const handleEditRow = useCallback(async (row) => {
    try {
      const res = await masterService.getObservationById(row.id || row.labObservationId);
      if (res.success && res.data) {
        const d = res.data;
        setFormData({
          id: d.id,
          labObservationId: d.labObservationId || '',
          name: d.name || '',
          readingFormat: d.readingFormat || 'Numeric',
          defaultValue: d.defaultValue || '',
          unit: d.unit || '',
          roundUpto: d.roundUpto ?? 2,
          minCritical: d.minCritical ?? '',
          maxCritical: d.maxCritical ?? '',
          formula: d.formula || '',
          resultRequired: d.resultRequired !== false,
          isComment: !!d.isComment,
          isActive: d.isActive !== false,
          ranges: d.ranges || []
        });
      } else {
        setFormData({
          ...INITIAL_OBSERVATION,
          id: row.id,
          labObservationId: row.labObservationId || '',
          name: row.name || '',
          readingFormat: row.readingFormat || 'Numeric',
          unit: row.unit || '',
          isActive: row.isActive !== false
        });
      }
      setErrors({});
      setIsEditMode(true);
      setViewMode('form');
    } catch (err) {
      showToast?.('Failed to load analyte details.', 'error');
    }
  }, [showToast]);

  // Range Table Helpers
  const handleAddRangeRow = () => {
    setFormData(prev => ({
      ...prev,
      ranges: [
        ...prev.ranges,
        {
          gender: 'B',
          fromAgeDays: 0,
          toAgeDays: 36500,
          minReading: '',
          maxReading: '',
          displayReading: ''
        }
      ]
    }));
  };

  const handleRemoveRangeRow = (index) => {
    setFormData(prev => ({
      ...prev,
      ranges: prev.ranges.filter((_, i) => i !== index)
    }));
  };

  const handleRangeChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.ranges];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, ranges: updated };
    });
  };

  // Validation
  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Analyte Name is required.';
    if (!formData.labObservationId.trim()) errs.labObservationId = 'Analyte Code is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        labObservationId: formData.labObservationId.trim().toUpperCase(),
        name: formData.name.trim(),
        unit: formData.unit.trim() || null,
        defaultValue: formData.defaultValue.trim() || null,
        minCritical: formData.minCritical !== '' ? parseFloat(formData.minCritical) : null,
        maxCritical: formData.maxCritical !== '' ? parseFloat(formData.maxCritical) : null,
        roundUpto: parseInt(formData.roundUpto, 10) || 2
      };

      let res;
      if (isEditMode) {
        res = await masterService.updateObservation(formData.id || formData.labObservationId, payload);
      } else {
        res = await masterService.createObservation(payload);
      }

      if (res.success) {
        showToast?.(`Observation analyte ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
        setViewMode('list');
        fetchObservations();
      } else {
        showToast?.(res.message || 'Operation failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error saving observation analyte.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Status Toggle
  const handleConfirmStatus = async () => {
    if (!confirmTarget) return;
    setStatusUpdating(true);
    try {
      const targetId = confirmTarget.id || confirmTarget.labObservationId;
      const newStatus = !confirmTarget.isActive;
      const res = await masterService.updateObservationStatus(targetId, newStatus);
      if (res.success) {
        showToast?.(`Analyte ${newStatus ? 'activated' : 'deactivated'}.`, 'success');
        setItems(prev => prev.map(x => (x.id === targetId || x.labObservationId === targetId) ? { ...x, isActive: newStatus } : x));
      } else {
        showToast?.(res.message || 'Status update failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error updating status.', 'error');
    } finally {
      setStatusUpdating(false);
      setConfirmTarget(null);
    }
  };

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? (isEditMode ? 'Update Observation Analyte' : 'Create Observation Analyte') : 'Observation & Analyte Master'}
      masterName="Analyte"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form' && !isEditMode) handleAddNew();
        else setViewMode(mode);
      }}
    >
      {/* ─── FULL-SCREEN FORM VIEW ─── */}
      {viewMode === 'form' && (
        <div className="glass-card fade-in">
          <form onSubmit={handleSubmit} noValidate>
            {/* Section 1: Analyte Identity */}
            <FormSection
              title="01. Identity & Format"
              iconColor="#4287f5"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              }
            >
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Observation Code <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.labObservationId ? 'border-danger' : ''}`}
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                    value={formData.labObservationId}
                    onChange={(e) => handleChange('labObservationId', e.target.value.toUpperCase())}
                    placeholder="e.g. OBS_HB, OBS_WBC"
                    required
                  />
                  {errors.labObservationId && <span className="error-message">{errors.labObservationId}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Observation / Analyte Name <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.name ? 'border-danger' : ''}`}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g. Hemoglobin, Total Leukocyte Count"
                    required
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Reading Format</label>
                  <select
                    className="form-select"
                    value={formData.readingFormat}
                    onChange={(e) => handleChange('readingFormat', e.target.value)}
                  >
                    {FORMAT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Unit of Measurement (UOM)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.unit}
                    onChange={(e) => handleChange('unit', e.target.value)}
                    placeholder="e.g. g/dL, mg/dL, /cumm, %"
                  />
                </div>
              </div>
            </FormSection>

            {/* Section 2: Values, Decimals & Formula */}
            <FormSection
              title="02. Measurement Limits & Calculation"
              iconColor="#10b981"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              }
            >
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Default Value / Preset</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.defaultValue}
                    onChange={(e) => handleChange('defaultValue', e.target.value)}
                    placeholder="Initial test value populated on screen"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Round Decimals</label>
                  <input
                    type="number"
                    min="0"
                    max="6"
                    className="form-input"
                    value={formData.roundUpto}
                    onChange={(e) => handleChange('roundUpto', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Min Critical / Panic Low Alert</label>
                  <input
                    type="number"
                    step="any"
                    className="form-input"
                    value={formData.minCritical}
                    onChange={(e) => handleChange('minCritical', e.target.value)}
                    placeholder="e.g. 5.0"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Max Critical / Panic High Alert</label>
                  <input
                    type="number"
                    step="any"
                    className="form-input"
                    value={formData.maxCritical}
                    onChange={(e) => handleChange('maxCritical', e.target.value)}
                    placeholder="e.g. 20.0"
                  />
                </div>

                {formData.readingFormat === 'Formula' && (
                  <div className="form-group col-span-2">
                    <label className="form-label">Calculation Formula</label>
                    <input
                      type="text"
                      className="form-input"
                      style={{ fontFamily: 'monospace' }}
                      value={formData.formula}
                      onChange={(e) => handleChange('formula', e.target.value)}
                      placeholder="e.g. (OBS_CHOL - OBS_HDL) / OBS_HDL"
                    />
                  </div>
                )}
              </div>
            </FormSection>

            {/* Section 3: Reference Ranges Table */}
            <FormSection
              title="03. Biological Reference Ranges (Normal Intervals)"
              iconColor="#f59e0b"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="8" y1="6" x2="21" y2="6" />
                  <line x1="8" y1="12" x2="21" y2="12" />
                  <line x1="8" y1="18" x2="21" y2="18" />
                  <line x1="3" y1="6" x2="3.01" y2="6" />
                  <line x1="3" y1="12" x2="3.01" y2="12" />
                  <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
              }
            >
              <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAddRangeRow}
                  style={{ fontSize: '12px', padding: '4px 10px' }}
                >
                  + Add Range Interval
                </button>
              </div>

              {formData.ranges.length === 0 ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', background: 'var(--bg-tertiary)', borderRadius: '8px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  No reference intervals configured. Click "+ Add Range Interval" to add age/gender biological normal limits.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="master-table mvc-table">
                    <thead>
                      <tr>
                        <th style={{ width: '120px' }}>Gender</th>
                        <th style={{ width: '120px' }}>From Age (Days)</th>
                        <th style={{ width: '120px' }}>To Age (Days)</th>
                        <th style={{ width: '130px' }}>Min Normal</th>
                        <th style={{ width: '130px' }}>Max Normal</th>
                        <th>Display String</th>
                        <th style={{ width: '60px', textAlign: 'center' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.ranges.map((rng, idx) => (
                        <tr key={idx}>
                          <td>
                            <select
                              className="form-select"
                              value={rng.gender || 'B'}
                              onChange={(e) => handleRangeChange(idx, 'gender', e.target.value)}
                            >
                              <option value="B">Both</option>
                              <option value="M">Male</option>
                              <option value="F">Female</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-input"
                              value={rng.fromAgeDays ?? 0}
                              onChange={(e) => handleRangeChange(idx, 'fromAgeDays', Number(e.target.value))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-input"
                              value={rng.toAgeDays ?? 36500}
                              onChange={(e) => handleRangeChange(idx, 'toAgeDays', Number(e.target.value))}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-input"
                              value={rng.minReading ?? ''}
                              onChange={(e) => handleRangeChange(idx, 'minReading', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-input"
                              value={rng.maxReading ?? ''}
                              onChange={(e) => handleRangeChange(idx, 'maxReading', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-input"
                              value={rng.displayReading ?? ''}
                              onChange={(e) => handleRangeChange(idx, 'displayReading', e.target.value)}
                              placeholder="e.g. 13.5 - 17.5 g/dL"
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => handleRemoveRangeRow(idx)}
                              style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1.1rem' }}
                              title="Delete row"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </FormSection>

            {/* Section 4: Status Flags */}
            <FormSection
              title="04. Status & Options"
              iconColor="#ec4899"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 11 12 14 22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.resultRequired}
                    onChange={(e) => handleChange('resultRequired', e.target.checked)}
                  />
                  <span><strong>Mandatory Result Entry</strong> (Technician cannot approve without filling value)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isComment}
                    onChange={(e) => handleChange('isComment', e.target.checked)}
                  />
                  <span><strong>Comment Analyte</strong> (Remarks or interpretive clinical text)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                  <span><strong>Analyte Active</strong> (Available for investigation linking and result entry)</span>
                </label>
              </div>
            </FormSection>

            {/* Footer */}
            <div className="form-actions-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setViewMode('list')}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ minWidth: '140px' }}
              >
                {saving ? 'Saving...' : (isEditMode ? 'Update Analyte' : 'Save Analyte')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── FULL-SCREEN GRID / LIST VIEW ─── */}
      {viewMode === 'list' && (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          {/* Filter Bar */}
          <div className="filter-bar">
            <div className="filter-bar-left">
              <div style={{ position: 'relative', width: '320px', minWidth: '240px' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ paddingLeft: '2.4rem' }}
                  placeholder="Search by code or analyte name..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>

              <select
                className="form-select"
                style={{ width: '180px' }}
                value={formatFilter}
                onChange={(e) => { setFormatFilter(e.target.value); setPage(1); }}
              >
                <option value="">All Formats</option>
                {FORMAT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>

              <select
                className="form-select"
                style={{ width: '140px' }}
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>

            <div className="filter-bar-right">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddNew}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>+ Add Observation Analyte</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="master-table mvc-table">
              <thead>
                <tr>
                  <th style={{ width: '60px', textAlign: 'center' }}>Sr.</th>
                  <th style={{ width: '130px' }}>Analyte Code</th>
                  <th>Observation / Analyte Name</th>
                  <th style={{ width: '130px' }}>Format</th>
                  <th style={{ width: '110px' }}>Unit</th>
                  <th style={{ width: '140px' }}>Critical Range</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '140px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      Loading observation analytes catalog...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      No observation analytes matching your criteria.
                    </td>
                  </tr>
                ) : (
                  items.map((r, index) => (
                    <tr key={r.id || r.labObservationId || index}>
                      <td style={{ textAlign: 'center', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                        {(page - 1) * pageSize + index + 1}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent)' }}>
                        {r.labObservationId}
                      </td>
                      <td>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{r.name}</span>
                      </td>
                      <td>
                        <span style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: '4px', backgroundColor: 'var(--bg-tertiary)' }}>
                          {r.readingFormat || 'Numeric'}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {r.unit || '—'}
                      </td>
                      <td style={{ fontSize: '0.825rem', fontFamily: 'monospace' }}>
                        {r.minCritical || r.maxCritical
                          ? `${r.minCritical ?? '—'} - ${r.maxCritical ?? '—'}`
                          : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge-status ${r.isActive ? 'active' : 'inactive'}`}>
                          {r.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '3px 8px', fontSize: '11.5px' }}
                            onClick={() => handleEditRow(r)}
                            title="Edit Analyte"
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{
                              padding: '3px 8px',
                              fontSize: '11.5px',
                              color: r.isActive ? '#ef4444' : '#10b981'
                            }}
                            onClick={() => setConfirmTarget(r)}
                            title={r.isActive ? 'Deactivate Analyte' : 'Activate Analyte'}
                          >
                            {r.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Standardized LIMS Centralized Pagination */}
          <CommonPagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            loading={loading}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={!!confirmTarget}
        title={confirmTarget?.isActive ? 'Deactivate Analyte' : 'Activate Analyte'}
        message={
          confirmTarget?.isActive
            ? `Are you sure you want to deactivate "${confirmTarget?.name}"?`
            : `Are you sure you want to activate "${confirmTarget?.name}"?`
        }
        confirmLabel={confirmTarget?.isActive ? 'Deactivate' : 'Activate'}
        variant={confirmTarget?.isActive ? 'danger' : 'primary'}
        loading={statusUpdating}
        onConfirm={handleConfirmStatus}
        onCancel={() => setConfirmTarget(null)}
      />
    </MasterPageWrapper>
  );
};

export default ObservationMasterPage;
